import { useCallback, useEffect, useRef, useState } from 'react';
import propTypes from 'prop-types';
import { Dialog, DialogPanel } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import {
    XMarkIcon, ChevronLeftIcon, ChevronRightIcon,
    MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';

const ESCALA_MIN = 1;
const ESCALA_MAX = 6;
const PASO = 0.5;

/**
 * Visor a pantalla completa de los documentos subidos.
 *
 * Existe por las fechas manuscritas del libro de familia: para distinguir un 7
 * de un 2 hace falta ampliar, y obligar al agente a abrir el fichero por su
 * cuenta rompe la revisión. Con zoom, arrastre y giro se comprueba sin salir
 * del formulario.
 */
const VisorImagenes = ({ imagenes, indice, onCerrar, onCambiar }) => {
    const { t } = useTranslation();
    const [escala, setEscala] = useState(1);
    const [rotacion, setRotacion] = useState(0);
    const [desplazamiento, setDesplazamiento] = useState({ x: 0, y: 0 });
    const arrastre = useRef(null);

    const actual = imagenes[indice];
    const hayVarias = imagenes.length > 1;

    const reiniciarVista = useCallback(() => {
        setEscala(1);
        setRotacion(0);
        setDesplazamiento({ x: 0, y: 0 });
    }, []);

    // Cada documento se abre desde cero: heredar el zoom del anterior despista.
    useEffect(() => { reiniciarVista(); }, [indice, reiniciarVista]);

    const ir = useCallback((salto) => {
        if (!hayVarias) return;
        onCambiar((indice + salto + imagenes.length) % imagenes.length);
    }, [hayVarias, indice, imagenes.length, onCambiar]);

    useEffect(() => {
        const alPulsar = (e) => {
            if (e.key === 'ArrowLeft') ir(-1);
            if (e.key === 'ArrowRight') ir(1);
            if (e.key === '+' || e.key === '=') setEscala((v) => Math.min(ESCALA_MAX, v + PASO));
            if (e.key === '-') setEscala((v) => Math.max(ESCALA_MIN, v - PASO));
            if (e.key === '0') reiniciarVista();
        };
        window.addEventListener('keydown', alPulsar);
        return () => window.removeEventListener('keydown', alPulsar);
    }, [ir, reiniciarVista]);

    const alRodar = (e) => {
        e.preventDefault();
        setEscala((v) => Math.min(ESCALA_MAX, Math.max(ESCALA_MIN, v - Math.sign(e.deltaY) * PASO)));
    };

    const empezarArrastre = (e) => {
        if (escala <= 1) return;
        arrastre.current = { x: e.clientX - desplazamiento.x, y: e.clientY - desplazamiento.y };
    };

    const moverArrastre = (e) => {
        if (!arrastre.current) return;
        setDesplazamiento({ x: e.clientX - arrastre.current.x, y: e.clientY - arrastre.current.y });
    };

    const soltarArrastre = () => { arrastre.current = null; };

    if (!actual) return null;

    const botonBarra = 'p-2 rounded-lg text-white/90 hover:bg-white/20 disabled:opacity-40 disabled:hover:bg-transparent transition-colors';

    return (
        <Dialog open={indice !== null} onClose={onCerrar} className="relative z-50">
            <div className="fixed inset-0 bg-black/80" aria-hidden="true" />

            <div className="fixed inset-0 flex flex-col">
                {/* Barra de herramientas */}
                <DialogPanel className="flex flex-col h-full">
                    <div className="flex items-center justify-between gap-4 px-4 py-3 bg-black/60 text-white">
                        <span className="min-w-0 text-sm truncate">
                            {actual.nombre}
                            {hayVarias && (
                                <span className="ml-2 text-white/60">{indice + 1} / {imagenes.length}</span>
                            )}
                        </span>

                        <div className="flex items-center gap-1 flex-shrink-0">
                            <button type="button" className={botonBarra} onClick={() => setEscala((v) => Math.max(ESCALA_MIN, v - PASO))} disabled={escala <= ESCALA_MIN} title={t('Alejar')}>
                                <MagnifyingGlassMinusIcon className="w-5 h-5" />
                            </button>
                            <span className="w-14 text-center text-sm tabular-nums text-white/80">{Math.round(escala * 100)}%</span>
                            <button type="button" className={botonBarra} onClick={() => setEscala((v) => Math.min(ESCALA_MAX, v + PASO))} disabled={escala >= ESCALA_MAX} title={t('Acercar')}>
                                <MagnifyingGlassPlusIcon className="w-5 h-5" />
                            </button>
                            <button type="button" className={botonBarra} onClick={() => setRotacion((r) => (r + 90) % 360)} title={t('Girar')}>
                                <ArrowPathIcon className="w-5 h-5" />
                            </button>
                            <button type="button" className={botonBarra} onClick={onCerrar} title={t('Cerrar')}>
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>
                    </div>

                    {/* Lienzo */}
                    <div
                        className="flex-1 relative overflow-hidden flex items-center justify-center"
                        onWheel={alRodar}
                        onMouseDown={empezarArrastre}
                        onMouseMove={moverArrastre}
                        onMouseUp={soltarArrastre}
                        onMouseLeave={soltarArrastre}
                        onDoubleClick={() => (escala > 1 ? reiniciarVista() : setEscala(2.5))}
                        style={{ cursor: escala > 1 ? (arrastre.current ? 'grabbing' : 'grab') : 'zoom-in' }}
                    >
                        <img
                            src={actual.url}
                            alt={actual.nombre}
                            draggable={false}
                            className="max-h-full max-w-full select-none transition-transform duration-75"
                            style={{
                                transform: `translate(${desplazamiento.x}px, ${desplazamiento.y}px) scale(${escala}) rotate(${rotacion}deg)`,
                            }}
                        />

                        {hayVarias && (
                            <>
                                <button type="button" onClick={() => ir(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 text-white hover:bg-black/70" title={t('Anterior')}>
                                    <ChevronLeftIcon className="w-6 h-6" />
                                </button>
                                <button type="button" onClick={() => ir(1)} className="absolute right-3 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 text-white hover:bg-black/70" title={t('Siguiente')}>
                                    <ChevronRightIcon className="w-6 h-6" />
                                </button>
                            </>
                        )}
                    </div>

                    {/* Tira de miniaturas */}
                    {hayVarias && (
                        <div className="flex gap-2 overflow-x-auto px-4 py-3 bg-black/60">
                            {imagenes.map((imagen, i) => (
                                <button
                                    key={imagen.nombre + i}
                                    type="button"
                                    onClick={() => onCambiar(i)}
                                    className={'flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-colors ' + (i === indice ? 'border-primary-500' : 'border-transparent hover:border-white/40')}
                                    title={imagen.nombre}
                                >
                                    <img src={imagen.url} alt={imagen.nombre} className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}

                    <p className="px-4 py-2 text-center text-xs text-white/50 bg-black/60">
                        {t('Rueda o +/− para el zoom · arrastra para mover · doble clic para acercar · ←/→ para cambiar · 0 para reiniciar')}
                    </p>
                </DialogPanel>
            </div>
        </Dialog>
    );
};

VisorImagenes.propTypes = {
    imagenes: propTypes.array.isRequired,
    indice: propTypes.number,
    onCerrar: propTypes.func.isRequired,
    onCambiar: propTypes.func.isRequired,
};

export default VisorImagenes;
